<template>
  <div>
    <!-- Hidden audio element -->
    <audio 
      v-if="mediaFile && mimeType"
      ref="audioRef"
      class="hidden"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @pause="handlePause"
    >
      <source :src="mediaFile" :type="mimeType" />
    </audio>

    <!-- Main content -->
    <div v-if="block && mediaFile && mimeType">
      <div class="flex flex-col w-full items-center my-5">
        <!-- Listen button when not playing -->
        <button 
          v-if="!playing"
          @click="handleListen" 
          :disabled="!videoPlaying || startTime === 0"
          :style="{
            backgroundColor: tourSettings.theme_color,
            color: tourSettings.theme_text_color
          }"
          :class="`flex relative mx-auto my-5 py-2 px-6 rounded-full ${!videoPlaying ? 'opacity-40' : 'opacity-100'}`"
        >
          <span :class="`absolute w-3 h-3 -mt-3.5 right-4 rounded-full ${readyState === 1 && deviceId ? 'bg-green-800' : 'bg-red-800'}`" />
          {{ $t('button.listen') }}
        </button>

        <!-- Pause button when playing -->
        <button 
          v-if="playing"
          @click="handlePause" 
          :disabled="!videoPlaying || startTime === 0"
          :style="{
            backgroundColor: tourSettings.theme_color,
            color: tourSettings.theme_text_color
          }"
          :class="`flex mx-auto my-5 py-2 px-6 rounded-full ${!playing ? 'opacity-40' : 'opacity-100'}`"
        >
          {{ $t('button.pause') }}
        </button>
      </div>

      <!-- Progress bar when playing -->
      <div v-if="playing" class="flex w-full">
        <div class="flex w-12">
          <time :dateTime="`P${Math.round(duration * played)}S`">
            {{ formatDuration(duration * played) }}
          </time>
        </div>
        <div class="flex flex-1 mx-2">
          <div class="progress">
            <div class="progress-done" :style="{ width: `${progressPercentage}%`, backgroundColor: tourSettings?.theme_color ?? '#000' }" />
          </div>
        </div>
        <div class="flex w-12">
          <time :dateTime="`P${Math.round(duration)}S`">
            {{ formatDuration(duration) }}
          </time>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moduleManager from '../modules/index.js'

export default {
  name: 'AudioSyncBlock',
  props: {
    block: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // WebSocket refs
      socket: null,
      reconnectTimeout: null,
      
      // Audio ref
      audioRef: null,
      
      // State
      videoPlaying: false,
      playing: false,
      deviceId: null,
      mediaFile: null,
      mimeType: null,
      duration: 0,
      played: 0,
      startTime: 0,
      connectionDelay: 0,
      pendingPlay: false,
      syncTimeout: null,
      readyState: WebSocket.CLOSED,
      
      // Game state
      gameState: null
    }
  },
  computed: {
    ...mapGetters('settings', ['tourSettings']),
    progressPercentage() {
      return (this.played * 100).toFixed(3);
    }
  },
  mounted() {
    this.loadGameState()
    
    // Listen for game state updates
    moduleManager.addEventListener('game:state_updated', this.loadGameState.bind(this))
    moduleManager.addEventListener('game:inventory_updated', this.loadGameState.bind(this))
  },
  beforeUnmount() {
    // Cleanup WebSocket when component unmounts
    if (this.socket) {
      this.socket.close(1000, 'leaving page')
      console.log('close WebSocket (leave page)')
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }
    
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout)
    }
    
    // Clean up event listeners
    moduleManager.removeEventListener('game:state_updated', this.loadGameState.bind(this))
    moduleManager.removeEventListener('game:inventory_updated', this.loadGameState.bind(this))
  },
  watch: {
    block: {
      handler(newBlock) {
        // Handle media
        if (newBlock?.data?.media && newBlock.data.media[this.$i18n.locale]) {
          const mediaData = newBlock.data.media[this.$i18n.locale]
          this.mediaFile = mediaData.preview_url
          
          // Set mime type
          if (mediaData.mime_type?.name) {
            this.mimeType = 'audio/' + mediaData.mime_type.name
          }
        }
        
        // Handle connection delay
        if (newBlock?.data?.device?.connection_delay !== null && newBlock?.data?.device?.connection_delay !== undefined) {
          this.connectionDelay = parseFloat(newBlock.data.device.connection_delay) || 0
          console.log("Connection delay set to:", this.connectionDelay, "seconds")
        } else {
          this.connectionDelay = 0
        }
        
        // Connect WebSocket after media data is loaded
        if (this.mediaFile && this.mimeType) {
          this.connectWebSocket()
        }
      },
      immediate: true
    }
  },
  methods: {
    loadGameState() {
      // Get game state from module
      const gameState = moduleManager.getModuleData('game')
      if (gameState) {
        this.gameState = gameState
      }
    },

    handleListen() {
      console.log("handleListen. send startAudio and start the player if not started.")
      this.socket.send(JSON.stringify({ event: 'startAudio', deviceId: this.deviceId }))

      if (this.$refs.audioRef.paused) {
        this.handlePlay()
      }
    },

    handleStop() {
      console.log('handleStop:stop playing.')
      this.$refs.audioRef.currentTime = 0
      this.videoPlaying = false
      this.handlePause()
    },

    handlePause() {
      console.log('handlePause: pause playing.')
      if (!this.$refs.audioRef.paused) {
        this.$refs.audioRef.pause()
      }
      this.playing = false
      this.pendingPlay = false
      
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout)
        this.syncTimeout = null
      }
    },

    handlePlay() {
      console.log('handlePlay:start playing.')
      if (this.$refs.audioRef) {
        const adjustedStartTime = parseFloat(this.startTime)
        
        if (adjustedStartTime < 0) {
          // We need to wait until the time becomes positive
          console.log("Start time is negative (" + adjustedStartTime + "), waiting for sync...")
          this.pendingPlay = true
          
          // Set a timeout to start playing when time becomes positive
          const waitTime = Math.abs(adjustedStartTime) * 1000 // Convert to milliseconds
          console.log("Will start playing in " + waitTime + "ms")
          
          this.syncTimeout = setTimeout(() => {
            this.startPlayback(0) // Start at 0 when we're synchronized
          }, waitTime)
        } else {
          // Start immediately if time is positive
          this.startPlayback(adjustedStartTime)
        }
      } else {
        this.playing = false
      }
    },

    startPlayback(startTime) {
      console.log('startPlayback: starting at time ' + startTime)
      this.pendingPlay = false
      
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout)
        this.syncTimeout = null
      }
      
      this.$refs.audioRef.play().then(() => {
        this.$refs.audioRef.currentTime = startTime
        this.playing = true
      }).catch(err => {
        console.error("Error starting playback: ", err)
        this.playing = false
      })
    },

    connectWebSocket() {
      const blockDataDeviceId = this.block?.data?.device?.device_id || null
      this.deviceId = blockDataDeviceId

      console.log("connectWebSocket")
      console.log("name", this.gameState?.name)
      console.log("block device_id", blockDataDeviceId)
      console.log("state device_id", this.deviceId)

     

      if (!this.block) {
        console.error("No block data found")
        return
      }

      if (!this.block.data?.device?.device_id) {
        console.error("No device_id found")
        return
      }

      if (!process.env.AUDIOSYNC_WEBSOCKET_URL) {
        console.error("No AUDIOSYNC_WEBSOCKET_URL found")
        return
      }

      // Initialize the WebSocket
      this.socket = new WebSocket(process.env.AUDIOSYNC_WEBSOCKET_URL)
      this.readyState = WebSocket.CONNECTING

      // Add event listeners
      this.socket.onopen = () => {
        console.log('WebSocket connected', this.block)
        this.readyState = WebSocket.OPEN

        // register listener
        if (blockDataDeviceId) {
          this.socket.send(JSON.stringify({ type: 'listen', deviceId: blockDataDeviceId }))
        }

        if (this.gameState?.name) {
          this.socket.send(JSON.stringify({ "name": this.gameState.name }))
        }
      }

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      this.socket.onclose = (event) => {
        console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`)
        this.readyState = WebSocket.CLOSED
        this.playing = false

        // Handle reconnection
        if (event.code !== 1000) { // Don't reconnect on normal closure
          console.log('Reconnecting WebSocket in 1 second...')
          this.reconnectTimeout = setTimeout(() => this.connectWebSocket(), 1000) // Reconnect after 1 second
        } else {
          console.log('Reconnect not required')
        }
      }

      this.socket.onmessage = (event) => {
        const message = JSON.parse(event.data)
        console.log("received message: ", message)

        if (message.type === 'pause') {
          this.handlePause()
        }

        if (message.event === 'waiting') {
          console.log("Waiting for video to start")
        }

        if (message.event === 'videoStarted') {
          // setVideoPlaying(true);
          // if (message.elapsedTime) {
          //     // we received a timecode.
          //     console.log("Received timecode. Update start position to " + parseFloat(message.elapsedTime) + " seconds");
          //
          //     //setStartTime(parseFloat(message.elapsedTime));
          // }
        }

        if (message.event === 'currentTime') {
          this.videoPlaying = true
          if (message.elapsedTime) {
            // we received a timecode.
            const rawElapsedTime = parseFloat(message.elapsedTime)
            const adjustedElapsedTime = rawElapsedTime - this.connectionDelay
            console.log("Received timecode. Raw elapsed time: " + rawElapsedTime + " seconds, connection delay: " + this.connectionDelay + " seconds, adjusted time: " + adjustedElapsedTime + " seconds")
            this.startTime = adjustedElapsedTime
            
            // If we're waiting for sync and the time is now positive, start playback
            if (this.pendingPlay && adjustedElapsedTime >= 0) {
              console.log("Sync achieved! Starting playback at time " + adjustedElapsedTime)
              this.startPlayback(adjustedElapsedTime)
            }
          }
        }

        if (message.event === 'videoStopped') {
          this.handleStop()
        }
      }
    },

    onLoadedMetadata(event) {
      console.log("audio metadata loaded", event.target.duration)
      this.duration = event.target.duration
    },

    onTimeUpdate(event) {
      let newPlayed = 0
      if (event.target.currentTime) {
        newPlayed = event.target.currentTime / event.target.duration
      }
      this.played = newPlayed
    },

    formatDuration(seconds) {
      const date = new Date(seconds * 1000)
      const hh = date.getUTCHours()
      const mm = date.getUTCMinutes()
      const ss = this.pad(date.getUTCSeconds())
      if (hh) {
        return `${hh}:${this.pad(mm)}:${ss}`
      }
      return `${mm}:${ss}`
    },

    pad(string) {
      return ('0' + string).slice(-2)
    }
  }
}
</script> 